import { Input } from '@/components/ui/input';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { WorkflowNode } from '@repo/shared-types';

interface GetReviewSummaryConfigProps {
  node: WorkflowNode;
  onDataChange: (nodeId: string, data: Record<string, unknown>) => void;
}

const toList = (value: string) =>
  value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

export const GetReviewSummaryConfig = ({ node, onDataChange }: GetReviewSummaryConfigProps) => {
  const data = node.data;

  const update = (field: string, value: unknown) => {
    onDataChange(node.id, { [field]: value });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={(data.label as string) ?? 'Review Summary'}
          onChange={(e) => update('label', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Date From</label>
        <VariableAutocomplete
          value={(data.dateFrom as string) ?? '{{date_offset:-1}}'}
          onChange={(v) => update('dateFrom', v)}
          placeholder="e.g. {{date_offset:-1}} (yesterday)"
          className="mt-1"
          inputClassName="font-mono"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {
            '{{date_offset:-1}} = yesterday, {{date_offset:0}} = today, {{date_offset:-7}} = 7 days ago'
          }
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Date To</label>
        <VariableAutocomplete
          value={(data.dateTo as string) ?? '{{date_offset:-1}}'}
          onChange={(v) => update('dateTo', v)}
          placeholder="e.g. {{date_offset:-1}}"
          className="mt-1"
          inputClassName="font-mono"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Count by (review field)</label>
        <Input
          value={(data.groupBy as string) ?? ''}
          onChange={(e) => update('groupBy', e.target.value.trim())}
          className="mt-1 font-mono"
          placeholder="interest_status"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          A field the review agent fills in, e.g. interest_status or reviewed_outcome.
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Detail fields</label>
        <Input
          value={((data.detailFields as string[] | undefined) ?? ['summary']).join(', ')}
          onChange={(e) => update('detailFields', toList(e.target.value))}
          className="mt-1 font-mono"
          placeholder="summary, notes"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          Review fields shown on each conversation row, separated by commas (up to 5).
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">
          Configuration IDs (optional)
        </label>
        <Input
          value={((data.configIds as string[] | undefined) ?? []).join(', ')}
          onChange={(e) => update('configIds', toList(e.target.value))}
          className="mt-1 font-mono"
          placeholder="Leave empty for all configurations"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={(data.outputKey as string) ?? 'review_summary'}
          onChange={(e) => update('outputKey', e.target.value)}
          className="mt-1 font-mono"
          placeholder="review_summary"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {
            'Use {{review_summary.html}} (counts + table) and {{review_summary.count}} in a Send Email node.'
          }
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">
          Call link base URL (optional)
        </label>
        <Input
          value={(data.callBaseUrl as string) ?? ''}
          onChange={(e) => update('callBaseUrl', e.target.value)}
          className="mt-1 font-mono"
          placeholder="https://your-app-url (defaults to FRONTEND_URL)"
        />
      </div>
    </div>
  );
};
